'use client';

import {
  EntitlementsProvider,
  useEntitlements,
} from '@contexts/EntitlementsContext';
import { useMicrocopy } from '@lib/cms/microcopy/client';
import {
  RECENTLY_VIEWED_ITEMS_PER_ROW,
  RECENTLY_VIEWED_MAX_ITEMS,
  useRecentlyViewed,
} from '@lib/recentlyViewed';
import { GetAssetByIdResponse } from '@lib/types/api/next-api/responses';
import { ReEngagement as ReEngagementType } from '@lib/types/cms/ReEngagement';
import { ProductRowCondensed, Separator } from 'ui-components';
import { usePackagesByIds } from '../ProductRow/usePackagesByIds';

type ReEngagementProps = {
  data: ReEngagementType;
};

type RecentlyViewedProps = {
  data: ReEngagementType;
  products: GetAssetByIdResponse[];
  skeletonCount: number;
};

const RecentlyViewed = ({
  data,
  products,
  skeletonCount,
}: RecentlyViewedProps) => {
  const { getUserEntitlement } = useEntitlements();
  const microcopy = useMicrocopy('product-card', {
    'product-card.free': 'Free',
    'product-card.owned': 'You own this asset',
  });

  return (
    <ProductRowCondensed
      products={products.map((product) => ({
        productId: product.id,
        name: product.name,
        productUrl: `/packages/package/${product.id}`,
        imageUrl: product.thumbnail,
        publisherId: product.publisher?.publisherId ?? '',
        publisherName: product.publisher?.name ?? '',
        publisherUrl: product.publisher
          ? `/publishers/${product.publisher.publisherId}`
          : '',
        price: {
          currentPrice: Number(product.price.currentPrice),
          originalPrice: Number(product.price.originalPrice),
          currency: product.price.currency,
          discountPercent: product.price.discountPercent,
        },
        isEntitled: !!getUserEntitlement(product.id),
      }))}
      openInNewTab={data.openInNewTab}
      skeletonCount={skeletonCount}
      microcopy={{
        title: data.recentlyViewedTitle ?? '',
        owned: microcopy['product-card.owned'] ?? '',
        free: microcopy['product-card.free'] ?? '',
      }}
    />
  );
};

const RecentlySaved = ({ data }: ReEngagementProps) => {
  const microcopy = useMicrocopy('product-card', {
    'product-card.free': 'Free',
    'product-card.owned': 'You own this asset',
  });

  // TODO to be implemented with wishlist
  return (
    <ProductRowCondensed
      products={[]}
      openInNewTab={data.openInNewTab}
      seeAllLink={data.recentlySavedSeeAllLink ?? '/'}
      microcopy={{
        title: data.recentlySavedTitle ?? '',
        seeAll: data.recentlySavedSeeAll ?? '',
        owned: microcopy['product-card.owned'] ?? '',
        free: microcopy['product-card.free'] ?? '',
      }}
    />
  );
};

const ReEngagement = ({ data }: ReEngagementProps) => {
  const { ids: productIDs } = useRecentlyViewed();
  const { data: products, error, isLoading } = usePackagesByIds(productIDs);

  const recentlyViewedProducts = error || !products ? [] : products;

  const showRecentlyViewed = data.showRecentlyViewed && productIDs.length > 0;
  const showRecentlySaved = data.showRecentlySaved;

  const skeletonCount = isLoading
    ? productIDs.length < RECENTLY_VIEWED_MAX_ITEMS
      ? RECENTLY_VIEWED_ITEMS_PER_ROW
      : RECENTLY_VIEWED_MAX_ITEMS
    : 0;

  if (!showRecentlyViewed && !showRecentlySaved) {
    return null;
  }

  return (
    <EntitlementsProvider>
      <div className="container">
        <div className="flex w-full flex-col justify-between gap-8 md:flex-row md:gap-2">
          {showRecentlyViewed && (
            <RecentlyViewed
              data={data}
              products={recentlyViewedProducts}
              skeletonCount={skeletonCount}
            />
          )}
          {showRecentlyViewed && showRecentlySaved && (
            <Separator
              orientation="vertical"
              className="mx-0 mt-10 hidden md:block md:h-[386px] xl:h-[186px]"
            />
          )}
          {showRecentlySaved && <RecentlySaved data={data} />}
        </div>
      </div>
    </EntitlementsProvider>
  );
};

export default ReEngagement;
